import type { RunResult } from "../systems/scoring.ts";

/**
 * DOM overlay UI (title, HUD, banners, pause, results). Kept in the DOM rather
 * than the canvas so text stays crisp on high-DPI phones and buttons get native
 * touch handling. The overlay is pointer-events:none except for interactive
 * controls, so gameplay touches pass straight through to the canvas.
 */
export class UI {
  readonly root: HTMLDivElement;

  // Callbacks wired by the game.
  onStart = (): void => {};
  onPauseToggle = (): void => {};
  onExit = (): void => {};
  onRetry = (): void => {};
  /** FLOW ON pressed on the level-clear card: advance to the next level. */
  onNext = (): void => {};
  onAudioToggle = (_on: boolean): void => {};
  onSelectLevel = (_idx: number): void => {};

  private titleEl: HTMLDivElement;
  private hudEl: HTMLDivElement;
  private bannerEl: HTMLDivElement;
  private resultsEl: HTMLDivElement;
  private pauseBtn: HTMLButtonElement;
  private pauseEl: HTMLDivElement;
  private levelSelectEl: HTMLDivElement;
  private levelListEl: HTMLDivElement;
  private levelBtns: HTMLButtonElement[] = [];
  private levelsData: { name: string; archetype: string }[] = [];
  private unlockedCount = 1;
  private bannerTimer?: number;
  /** All AUDIO: ON/OFF buttons (title + pause) — kept in sync on toggle. */
  private audioBtns: HTMLButtonElement[] = [];

  private livesEl: HTMLDivElement;
  private scoreEl: HTMLDivElement;
  private multEl: HTMLDivElement;
  private levelEl: HTMLDivElement;
  private progressFill: HTMLDivElement;
  private audioOn = true;

  constructor(mount: HTMLElement) {
    injectStyles();
    this.root = el("div", "fs-ui");
    mount.appendChild(this.root);

    // --- HUD ---
    this.hudEl = el("div", "fs-hud fs-hidden");
    this.levelEl = el("div", "fs-level");
    this.scoreEl = el("div", "fs-score");
    this.multEl = el("div", "fs-mult");
    this.livesEl = el("div", "fs-lives");
    const progress = el("div", "fs-progress");
    this.progressFill = el("div", "fs-progress-fill");
    progress.appendChild(this.progressFill);
    const topRow = el("div", "fs-hud-top");
    topRow.append(this.levelEl, this.multEl, this.scoreEl);
    this.hudEl.append(topRow, this.livesEl, progress);

    this.pauseBtn = btn("II", "fs-pause-btn fs-hidden", () => this.onPauseToggle());

    // --- Title ---
    this.titleEl = el("div", "fs-screen fs-title");
    const logo = el("div", "fs-logo");
    logo.textContent = "FLOWSTATE";
    const tagline = el("div", "fs-tagline");
    tagline.textContent = "evade · carve · ascend";
    const startBtn = btn("TAP TO BEGIN", "fs-btn fs-btn-primary", () => this.onStart());
    const selectBtn = btn("LEVEL SELECT", "fs-btn fs-btn-ghost", () => this.showLevelSelectScreen());
    const how = el("div", "fs-how");
    how.innerHTML =
      "Drag to move — your dot floats above your finger.<br/>" +
      "Slip the tiny white core through the gaps. Graze for score.";
    this.titleEl.append(logo, tagline, startBtn, selectBtn, this.makeAudioBtn(), how);

    // --- Level select ---
    this.levelSelectEl = el("div", "fs-screen fs-levels fs-hidden");
    const lsTitle = el("div", "fs-logo fs-logo-sm");
    lsTitle.textContent = "SELECT LEVEL";
    this.levelListEl = el("div", "fs-level-list");
    const lsBack = btn("BACK", "fs-btn fs-btn-ghost", () => this.showTitle());
    this.levelSelectEl.append(lsTitle, this.levelListEl, lsBack);

    // --- Banner (level transitions) ---
    this.bannerEl = el("div", "fs-banner fs-hidden");

    // --- Pause ---
    this.pauseEl = el("div", "fs-screen fs-pause fs-hidden");
    const pTitle = el("div", "fs-logo fs-logo-sm");
    pTitle.textContent = "PAUSED";
    const resumeBtn = btn("RESUME", "fs-btn fs-btn-primary", () => this.onPauseToggle());
    const exitBtn = btn("EXIT", "fs-btn fs-btn-ghost", () => this.onExit());
    this.pauseEl.append(pTitle, resumeBtn, this.makeAudioBtn(), exitBtn);

    // --- Results ---
    this.resultsEl = el("div", "fs-screen fs-results fs-hidden");

    this.root.append(
      this.hudEl,
      this.pauseBtn,
      this.titleEl,
      this.levelSelectEl,
      this.bannerEl,
      this.pauseEl,
      this.resultsEl,
    );
  }

  /** An AUDIO: ON/OFF toggle; every instance shares state and stays in sync. */
  private makeAudioBtn(): HTMLButtonElement {
    const label = () => (this.audioOn ? "AUDIO: ON" : "AUDIO: OFF");
    const b = btn(label(), "fs-btn fs-btn-ghost", () => {
      this.audioOn = !this.audioOn;
      for (const x of this.audioBtns) x.textContent = label();
      this.onAudioToggle(this.audioOn);
    });
    this.audioBtns.push(b);
    return b;
  }

  /** Populate the level-select list. Call once after construction. */
  setLevels(levels: { name: string; archetype: string }[]): void {
    this.levelsData = levels;
    this.levelListEl.innerHTML = "";
    this.levelBtns = levels.map((_lvl, i) => {
      const b = document.createElement("button");
      b.className = "fs-level-btn";
      b.addEventListener("click", (e) => {
        e.preventDefault();
        if (b.disabled) return;
        this.onSelectLevel(i);
      });
      this.levelListEl.appendChild(b);
      return b;
    });
    this.renderLevelButtons();
  }

  /** Update how many levels (from the start) are unlocked/selectable. */
  setUnlocked(count: number): void {
    this.unlockedCount = count;
    this.renderLevelButtons();
  }

  private renderLevelButtons(): void {
    this.levelBtns.forEach((b, i) => {
      const lvl = this.levelsData[i];
      const unlocked = i < this.unlockedCount;
      b.disabled = !unlocked;
      b.classList.toggle("fs-level-locked", !unlocked);
      b.innerHTML = unlocked
        ? `<span class="fs-level-num">${String(i + 1).padStart(2, "0")}</span>` +
          `<span class="fs-level-name">${lvl.name}</span>` +
          `<span class="fs-level-arch">${lvl.archetype}</span>`
        : `<span class="fs-level-num">${String(i + 1).padStart(2, "0")}</span>` +
          `<span class="fs-level-name">LOCKED</span>` +
          `<span class="fs-level-lock">🔒</span>`;
    });
  }

  private showLevelSelectScreen(): void {
    this.titleEl.classList.add("fs-hidden");
    this.levelSelectEl.classList.remove("fs-hidden");
  }

  showTitle(): void {
    this.titleEl.classList.remove("fs-hidden");
    this.levelSelectEl.classList.add("fs-hidden");
    this.hudEl.classList.add("fs-hidden");
    this.pauseBtn.classList.add("fs-hidden");
    this.resultsEl.classList.add("fs-hidden");
  }

  startGameUI(): void {
    this.titleEl.classList.add("fs-hidden");
    this.levelSelectEl.classList.add("fs-hidden");
    this.resultsEl.classList.add("fs-hidden");
    this.hudEl.classList.remove("fs-hidden");
    this.pauseBtn.classList.remove("fs-hidden");
  }

  setPaused(on: boolean): void {
    this.pauseEl.classList.toggle("fs-hidden", !on);
    this.pauseBtn.textContent = on ? "▶" : "II";
  }

  updateHud(
    level: string,
    score: number,
    mult: number,
    lives: number,
    maxLives: number,
    progress: number,
  ): void {
    this.levelEl.textContent = level;
    this.scoreEl.textContent = String(Math.floor(score)).padStart(6, "0");
    this.multEl.textContent = "×" + mult;
    this.progressFill.style.width = (progress * 100).toFixed(1) + "%";
    let dots = "";
    for (let i = 0; i < maxLives; i++) dots += i < lives ? "●" : "○";
    this.livesEl.textContent = dots;
  }

  banner(title: string, sub = "", ms = 2200): void {
    this.bannerEl.innerHTML = `<div class="fs-banner-title">${title}</div>${
      sub ? `<div class="fs-banner-sub">${sub}</div>` : ""
    }`;
    this.bannerEl.classList.remove("fs-hidden");
    this.bannerEl.classList.add("fs-banner-show");
    // Cancel the previous banner's hide timer so back-to-back banners
    // (e.g. SWORD UNLOCKED → PYRE) each get their full display time.
    if (this.bannerTimer !== undefined) window.clearTimeout(this.bannerTimer);
    this.bannerTimer = window.setTimeout(() => {
      this.bannerEl.classList.remove("fs-banner-show");
      this.bannerEl.classList.add("fs-hidden");
      this.bannerTimer = undefined;
    }, ms);
  }

  /**
   * The end-of-level card: FLOW COMPLETE, the stats earned within just that
   * level, a gold-star celebration if it was a no-hit PERFECT run, and a
   * FLOW ON button to the next one. No letter grades — just your stats.
   */
  showLevelClear(r: RunResult, levelName: string): void {
    this.hudEl.classList.add("fs-hidden");
    this.pauseBtn.classList.add("fs-hidden");
    const mm = Math.floor(r.timeSurvived / 60);
    const ss = Math.floor(r.timeSurvived % 60);
    this.resultsEl.innerHTML = `
      <div class="fs-results-head">FLOW COMPLETE</div>
      <div class="fs-results-sub">${levelName} · LIVES RESTORED</div>
      ${r.perfect ? perfectBadge() : ""}
      <div class="fs-stats">
        <div><span>SCORE</span><b>${r.score.toLocaleString()}</b></div>
        <div><span>KO'S</span><b>${r.kills}</b></div>
        <div><span>HITS</span><b>${r.hits}</b></div>
        <div><span>TIME</span><b>${mm}:${String(ss).padStart(2, "0")}</b></div>
      </div>`;
    const next = btn("FLOW ON", "fs-btn fs-btn-primary", () => this.onNext());
    this.resultsEl.appendChild(next);
    this.resultsEl.classList.remove("fs-hidden");
  }

  showResults(r: RunResult, headline: string, levelProgress = 0): void {
    this.hudEl.classList.add("fs-hidden");
    this.pauseBtn.classList.add("fs-hidden");
    const mm = Math.floor(r.timeSurvived / 60);
    const ss = Math.floor(r.timeSurvived % 60);
    // A no-hit clear gets the gold-star PERFECT celebration; a broken run
    // gets the pink flow-ring showing how far it reached. No letter grades.
    const topHtml = r.cleared ? (r.perfect ? perfectBadge() : "") : brokenRing(levelProgress);
    this.resultsEl.innerHTML = `
      <div class="fs-results-head">${headline}</div>
      ${topHtml}
      <div class="fs-stats">
        <div><span>SCORE</span><b>${r.score.toLocaleString()}</b></div>
        <div><span>KO'S</span><b>${r.kills}</b></div>
        <div><span>HITS</span><b>${r.hits}</b></div>
        <div><span>TIME</span><b>${mm}:${String(ss).padStart(2, "0")}</b></div>
      </div>`;
    const retry = btn("PLAY AGAIN", "fs-btn fs-btn-primary", () => this.onRetry());
    this.resultsEl.appendChild(retry);
    this.resultsEl.classList.remove("fs-hidden");
  }
}

/**
 * The PERFECT-run celebration: three pulsing gold stars popping in over a
 * soft glowing burst, with a PERFECT RUN title underneath. Shown in place of
 * any rank/grade — the only thing FLOWSTATE calls out by name is a clean,
 * no-hit clear.
 */
function perfectBadge(): string {
  return `
    <div class="fs-perfect-badge">
      <div class="fs-perfect-glow"></div>
      <div class="fs-stars">
        <span class="fs-star" style="animation-delay:0s,.5s">&#9733;</span>
        <span class="fs-star" style="animation-delay:.12s,.62s">&#9733;</span>
        <span class="fs-star" style="animation-delay:.24s,.74s">&#9733;</span>
      </div>
      <div class="fs-perfect-title">PERFECT RUN</div>
    </div>`;
}

/**
 * The FLOW BROKEN ring: a dimly dotted circle (the dissipated flow) with a
 * glowing pink arc traced over it showing how much of the level the run
 * cleared, and the percentage in the middle.
 */
function brokenRing(progress: number): string {
  const frac = Math.max(0, Math.min(1, progress));
  const R = 64;
  const C = 2 * Math.PI * R;
  return `
    <div class="fs-ring-wrap">
      <svg class="fs-ring" width="170" height="170" viewBox="0 0 170 170">
        <circle cx="85" cy="85" r="${R}" fill="none" stroke="#41506a" stroke-width="7"
          stroke-dasharray="2.5 13" stroke-linecap="round" opacity="0.75"/>
        <circle cx="85" cy="85" r="${R}" fill="none" stroke="#ff5b7f" stroke-width="9"
          stroke-linecap="round" stroke-dasharray="${(frac * C).toFixed(1)} ${C.toFixed(1)}"
          transform="rotate(-90 85 85)" class="fs-ring-fill"/>
      </svg>
      <div class="fs-ring-label">
        <div class="fs-ring-pct">${Math.round(frac * 100)}%</div>
        <div class="fs-ring-sub">COMPLETE</div>
      </div>
    </div>`;
}

function el(tag: string, cls: string): HTMLDivElement {
  const e = document.createElement(tag) as HTMLDivElement;
  e.className = cls;
  return e;
}

function btn(text: string, cls: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.className = cls;
  b.textContent = text;
  b.addEventListener("click", (e) => {
    e.preventDefault();
    onClick();
  });
  return b;
}

function injectStyles(): void {
  if (document.getElementById("fs-ui-styles")) return;
  const s = document.createElement("style");
  s.id = "fs-ui-styles";
  s.textContent = CSS;
  document.head.appendChild(s);
}

const CSS = `
.fs-ui{position:fixed;inset:0;pointer-events:none;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,system-ui,sans-serif;color:#eaf2ff;z-index:10;}
.fs-ui button{pointer-events:auto;cursor:pointer;font-family:inherit;}
.fs-hidden{display:none !important;}
.fs-screen{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;text-align:center;padding:24px;background:radial-gradient(120% 80% at 50% 40%, rgba(8,10,18,.72), rgba(5,6,10,.94));backdrop-filter:blur(2px);}
.fs-logo{font-size:clamp(40px,12vw,86px);font-weight:800;letter-spacing:.14em;color:#36f9d0;text-shadow:0 0 24px rgba(54,249,208,.5);}
.fs-logo-sm{font-size:clamp(28px,8vw,52px);}
.fs-tagline{font-size:clamp(12px,3.4vw,17px);letter-spacing:.32em;color:#9fb2c8;text-transform:uppercase;}
.fs-how{max-width:440px;font-size:clamp(12px,3.3vw,15px);line-height:1.7;color:#9fb2c8;}
.fs-btn{border:2px solid #36f9d0;background:transparent;color:#36f9d0;font-weight:700;letter-spacing:.16em;padding:14px 30px;border-radius:40px;font-size:clamp(13px,3.6vw,16px);transition:transform .08s,background .15s;}
.fs-btn:active{transform:scale(.95);}
.fs-btn-primary{background:#36f9d0;color:#05060a;box-shadow:0 0 30px rgba(54,249,208,.4);}
.fs-btn-ghost{border-color:#41506a;color:#9fb2c8;padding:9px 18px;font-size:12px;}
.fs-hud{position:absolute;inset:0;padding:max(14px,env(safe-area-inset-top)) 16px 16px;}
.fs-hud-top{display:flex;justify-content:space-between;align-items:center;gap:10px;font-variant-numeric:tabular-nums;}
.fs-level{font-size:13px;letter-spacing:.22em;color:#9fb2c8;font-weight:700;}
.fs-score{font-size:20px;font-weight:800;letter-spacing:.08em;color:#eaf2ff;}
.fs-mult{font-size:16px;font-weight:800;color:#ffd166;}
.fs-lives{margin-top:8px;font-size:15px;letter-spacing:.2em;color:#36f9d0;}
.fs-progress{position:absolute;left:16px;right:16px;bottom:max(14px,env(safe-area-inset-bottom));height:4px;background:rgba(90,100,120,.25);border-radius:3px;overflow:hidden;}
.fs-progress-fill{height:100%;width:0%;background:linear-gradient(90deg,#36f9d0,#ffd166);border-radius:3px;transition:width .12s linear;}
.fs-pause-btn{position:absolute;bottom:calc(max(14px,env(safe-area-inset-bottom)) + 16px);left:14px;width:42px;height:42px;border-radius:50%;border:2px solid #41506a;background:rgba(8,10,18,.6);color:#9fb2c8;font-weight:800;font-size:14px;}
.fs-banner{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;text-align:center;opacity:0;transition:opacity .35s;pointer-events:none;}
.fs-banner-show{opacity:1;}
.fs-banner-title{font-size:clamp(30px,9vw,64px);font-weight:800;letter-spacing:.1em;color:#36f9d0;text-shadow:0 0 26px rgba(54,249,208,.55);}
.fs-banner-sub{font-size:clamp(13px,3.6vw,18px);letter-spacing:.26em;color:#9fb2c8;text-transform:uppercase;}
.fs-ring-wrap{position:relative;width:170px;height:170px;display:flex;align-items:center;justify-content:center;}
.fs-ring-fill{filter:drop-shadow(0 0 10px rgba(255,91,127,.65));}
.fs-ring-label{position:absolute;text-align:center;}
.fs-ring-pct{font-size:36px;font-weight:900;color:#ff5b7f;text-shadow:0 0 18px rgba(255,91,127,.45);font-variant-numeric:tabular-nums;}
.fs-ring-sub{font-size:10px;letter-spacing:.3em;color:#9fb2c8;margin-top:2px;}
.fs-results-head{font-size:clamp(18px,5vw,28px);font-weight:800;letter-spacing:.16em;color:#eaf2ff;}
.fs-results-sub{font-size:clamp(11px,3vw,14px);letter-spacing:.26em;color:#9fb2c8;text-transform:uppercase;margin-top:-8px;}
.fs-perfect-badge{position:relative;display:flex;flex-direction:column;align-items:center;gap:8px;padding:10px 0;}
.fs-perfect-glow{position:absolute;inset:-36px;background:radial-gradient(circle,rgba(255,209,102,.35),transparent 70%);filter:blur(6px);pointer-events:none;animation:fs-glow-pulse 1.8s ease-in-out infinite;}
.fs-stars{display:flex;gap:12px;font-size:42px;line-height:1;position:relative;}
.fs-star{display:inline-block;color:#ffd166;text-shadow:0 0 16px rgba(255,209,102,.8),0 0 32px rgba(255,209,102,.5);animation:fs-star-pop .5s ease-out backwards,fs-star-twinkle 1.4s ease-in-out infinite;}
.fs-perfect-title{position:relative;font-size:clamp(16px,4.5vw,22px);font-weight:900;letter-spacing:.28em;color:#ffd166;text-shadow:0 0 20px rgba(255,209,102,.6);}
@keyframes fs-star-pop{0%{transform:scale(0) rotate(-30deg);opacity:0;}70%{transform:scale(1.3) rotate(10deg);opacity:1;}100%{transform:scale(1) rotate(0);opacity:1;}}
@keyframes fs-star-twinkle{0%,100%{filter:brightness(1);}50%{filter:brightness(1.6);}}
@keyframes fs-glow-pulse{0%,100%{opacity:.5;transform:scale(1);}50%{opacity:.9;transform:scale(1.1);}}
.fs-stats{display:grid;grid-template-columns:repeat(2,minmax(120px,1fr));gap:10px 28px;margin:8px 0 6px;}
.fs-stats div{display:flex;justify-content:space-between;gap:18px;font-size:15px;border-bottom:1px solid rgba(90,100,120,.25);padding:6px 0;}
.fs-stats span{color:#9fb2c8;letter-spacing:.12em;}
.fs-stats b{color:#eaf2ff;font-variant-numeric:tabular-nums;}
.fs-level-list{display:flex;flex-direction:column;gap:10px;width:100%;max-width:380px;}
.fs-level-btn{display:flex;align-items:center;gap:14px;width:100%;text-align:left;border:2px solid #36f9d0;background:rgba(54,249,208,.08);color:#eaf2ff;border-radius:14px;padding:12px 16px;font-weight:700;letter-spacing:.05em;transition:transform .08s,background .15s;}
.fs-level-btn:active{transform:scale(.97);}
.fs-level-btn:disabled,.fs-level-locked{border-color:#41506a;background:rgba(90,100,120,.08);color:#5a6478;cursor:default;}
.fs-level-num{font-size:20px;font-weight:900;color:#36f9d0;min-width:34px;}
.fs-level-btn:disabled .fs-level-num{color:#5a6478;}
.fs-level-name{flex:1;font-size:15px;}
.fs-level-arch{font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:#9fb2c8;}
.fs-level-lock{font-size:16px;opacity:.7;}
`;
